import React, { useState } from 'react';

const IndicatorTag = ({ indicator, onClick, isActive = false }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { name, hasData, status } = indicator;

  // Determine colors based on status - match Status Breakdown palette (pastel fill + colored border/text)
  let bgColor, textColor, borderColor;
  
  if (!hasData) {
    bgColor = 'white';
    textColor = '#2c3e50';
    borderColor = '#b0b0b0';
  } else if (status === 'high' || status === 'critical') {
    bgColor = 'linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%)';
    textColor = '#000000';
    borderColor = '#c0392b';
  } else if (status === 'warning') {
    bgColor = 'linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)';
    textColor = '#000000';
    borderColor = '#f39c12';
  } else if (status === 'normal') {
    bgColor = 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)';
    textColor = '#000000';
    borderColor = '#27ae60';
  } else {
    // Fallback for hasData but no status
    bgColor = 'white';
    textColor = '#2c3e50';
    borderColor = '#b0b0b0';
  }

  // Determine hover background - matching IndicatorTabs hover behavior
  const getHoverBackground = () => {
    if (!hasData) {
      // No data indicators don't change on hover
      return bgColor;
    }
    if (isHovered && hasData) {
      if (status === 'high' || status === 'critical') {
        return 'linear-gradient(135deg, #f5c6cb 0%, #f1b0b7 100%)';
      } else if (status === 'warning') {
        return 'linear-gradient(135deg, #ffe8a1 0%, #ffdf7e 100%)';
      } else if (status === 'normal') {
        return 'linear-gradient(135deg, #c3e6cb 0%, #b2dfbe 100%)';
      } else {
        // No status - use blue gradient on hover like IndicatorTabs
        return 'linear-gradient(135deg, #f0f7ff 0%, #e3f2fd 100%)';
      }
    }
    return bgColor;
  };

  // Handle active state styling
  const getActiveStyle = () => {
    if (!isActive || !hasData) return {};
    
    if (status === 'high' || status === 'critical') {
      return {
        background: 'linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%)',
        color: '#000000',
        borderColor: '#c0392b',
        borderWidth: '3px',
        boxShadow: '0 4px 12px rgba(192, 57, 43, 0.25)'
      };
    } else if (status === 'warning') {
      return {
        background: 'linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)',
        color: '#000000',
        borderColor: '#f39c12',
        borderWidth: '3px',
        boxShadow: '0 4px 12px rgba(243, 156, 18, 0.25)'
      };
    } else if (status === 'normal') {
      return {
        background: 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)',
        color: '#000000',
        borderColor: '#27ae60',
        borderWidth: '3px',
        boxShadow: '0 4px 12px rgba(39, 174, 96, 0.25)'
      };
    } else {
      return {
        background: 'linear-gradient(135deg, var(--accent) 0%, #2980b9 100%)',
        color: 'white',
        borderColor: 'var(--accent)',
        borderWidth: '3px',
        boxShadow: '0 4px 12px rgba(52, 152, 219, 0.3)'
      };
    }
  };

  const activeStyle = getActiveStyle();
  const baseStyle = {
    padding: '10px 12px',
    background: isActive && activeStyle.background ? activeStyle.background : getHoverBackground(),
    borderRadius: '6px',
    color: isActive && activeStyle.color ? activeStyle.color : (isHovered && hasData && !status ? 'var(--accent)' : textColor),
    fontWeight: (isActive || (hasData && status)) ? 700 : 500,
    border: isActive && activeStyle.borderColor
      ? `${activeStyle.borderWidth || '3px'} solid ${activeStyle.borderColor}`
      : `${hasData ? '2px' : '3px'} solid ${isHovered && hasData && !status ? 'var(--accent)' : borderColor}`,
    cursor: hasData ? 'pointer' : 'default',
    opacity: hasData ? 1 : 0.6,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    userSelect: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '0.5rem',
    fontSize: '0.8rem',
    lineHeight: 1.3,
    letterSpacing: '0.2px',
    position: 'relative',
    transform: isActive ? 'translateY(-1px)' : (isHovered && hasData ? 'translateY(-2px)' : 'translateY(0)'),
    boxShadow: isActive && activeStyle.boxShadow
      ? activeStyle.boxShadow
        : (isHovered && hasData 
        ? (status === 'high' || status === 'critical' ? '0 4px 12px rgba(192, 57, 43, 0.2)' 
           : status === 'warning' ? '0 4px 12px rgba(243, 156, 18, 0.2)'
           : status === 'normal' ? '0 4px 12px rgba(39, 174, 96, 0.2)'
           : '0 4px 12px rgba(52, 152, 219, 0.2)')
        : hasData 
          ? '0 2px 4px rgba(0,0,0,0.1)'
          : '0 2px 6px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.1)')
  };

  return (
    <button
      style={baseStyle}
      onClick={hasData ? onClick : undefined}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      title={hasData ? `Click to view ${name} data on map` : 'Data is not available for this indicator'}
      aria-label={hasData ? `View ${name}` : `${name} - Data not available`}
      disabled={!hasData}
    >
      <div style={{
        flex: 1,
        minWidth: 0,
        fontWeight: 500,
        letterSpacing: '0.2px',
        textAlign: 'left'
      }}>
        {name}
      </div>
      {!hasData && (
        <img 
          src="/lock.png" 
          alt="Locked" 
          style={{
            width: '16px',
            height: '16px',
            flexShrink: 0,
            opacity: 0.7,
            objectFit: 'contain'
          }}
        />
      )}
    </button>
  );
};

export default IndicatorTag;

